/*
 * ⚠ Import entre features, permitido SOLO aquí: la composición usa `/hotels`
 * (Onboarding) y `/me` (sesión). Con mocks apagados esto es un no-op.
 */
// eslint-disable-next-line no-restricted-imports
import { registerOnboardingMocks } from '@/features/onboarding/api/onboardingMocks'
import '@/app/sessionApi'
import { registerMockRoutes, type MockRoute } from '@/shared/lib/mockBaseQuery'
import type {
  ApiEnvelope,
  AssignmentApi,
  CatalogItemApi,
  PaginatedEnvelope,
  ParticipantApi,
  ParticipationResultApi,
  RequisitionApi,
  RequisitionJournalEntryApi,
  RequisitionPositionApi,
  StatusRefApi,
} from '@/shared/types/apiContract.types'

/**
 * Fixtures de `demand` en la forma CRUDA del contrato real: requisiciones con
 * sus posiciones y los catálogos del alta. Los hoteles son los clientes de
 * Onboarding (`htl-psp-0012` …).
 */

// ── Catálogos, con los códigos del seed real ─────────────────────────────────
const DEPARTMENTS: CatalogItemApi[] = [
  { id: 'dep-hk', code: 'HOUSEKEEPING', name: 'Housekeeping' },
  { id: 'dep-fb', code: 'FOOD_BEVERAGE', name: 'Alimentos' },
  { id: 'dep-fd', code: 'FRONT_DESK', name: 'Front Desk' },
  /* Donde se piden las posiciones sin departamento (Hugo, 2026-10-27). */
  { id: 'dep-general', code: 'GENERAL', name: 'General' },
]

const POSITIONS: CatalogItemApi[] = [
  { id: 'pos-hk', code: 'HOUSEKEEPER', name: 'Housekeeper' },
  { id: 'pos-hm', code: 'HOUSEMAN', name: 'Houseman' },
  { id: 'pos-ln', code: 'LAUNDRY', name: 'Laundry' },
  { id: 'pos-ck', code: 'COOK', name: 'Cocinero' },
  { id: 'pos-fk', code: 'WH_FORKLIFT_OPERATOR', name: 'Forklift Operator' },
  { id: 'pos-cashier', code: 'RETAIL_CASHIER', name: 'Cashier' },
]

/** Igual que `catalogs.position.hotel_department_id`: cada puesto es de UN depto,
    y desde el 2026-10-17 puede no tener ninguno (industrias fuera de Hospitality). */
const POSITION_DEPARTMENT: Record<string, string> = {
  'pos-hk': 'dep-hk',
  'pos-hm': 'dep-hk',
  'pos-ln': 'dep-hk',
  'pos-ck': 'dep-fb',
}

/** La industria que cotiza el puesto (2026-10-17): Oranje deja de ser solo hoteles. */
const INDUSTRIES: CatalogItemApi[] = [
  { id: 'ind-hosp', code: 'HOSPITALITY', name: 'Hospitality' },
  { id: 'ind-wh', code: 'WAREHOUSE_LOGISTICS', name: 'Warehouse & Logistics' },
  { id: 'ind-retail', code: 'RETAIL', name: 'Retail' },
]

const POSITION_INDUSTRY: Record<string, string> = {
  'pos-hk': 'ind-hosp',
  'pos-hm': 'ind-hosp',
  'pos-ln': 'ind-hosp',
  'pos-ck': 'ind-hosp',
  'pos-fk': 'ind-wh',
  'pos-cashier': 'ind-retail',
}

/** Sin contrato vigente: los registrados sin ciclo comercial de Onboarding (`htl-0002` es el cliente sin contrato). */
const HOTELS_WITHOUT_CONTRACT = new Set(['htl-0001', 'htl-0002'])

/** Solo para las pruebas: que un hotel con requisiciones se quede sin contrato vigente. */
export function setMockHotelContract(hotelId: string, hasContract: boolean): void {
  if (hasContract) HOTELS_WITHOUT_CONTRACT.delete(hotelId)
  else HOTELS_WITHOUT_CONTRACT.add(hotelId)
}

const MODALITIES: CatalogItemApi[] = [
  { id: 'mod-ft', code: 'FULL_TIME', name: 'Tiempo completo' },
  { id: 'mod-pt', code: 'PART_TIME', name: 'Medio tiempo' },
  { id: 'mod-tp', code: 'TEMPORARY', name: 'Temporal' },
  { id: 'mod-or', code: 'ON_REQUEST', name: 'Según solicitud' },
]

const ENGLISH: CatalogItemApi[] = [
  { id: 'eng-ba', code: 'BASIC', name: 'Básico' },
  { id: 'eng-in', code: 'INTERMEDIATE', name: 'Intermedio' },
  { id: 'eng-ad', code: 'ADVANCED', name: 'Avanzado' },
  { id: 'eng-co', code: 'CONVERSATIONAL', name: 'Conversacional' },
]

/** Conceptos de pago extra del ajuste eventual (Hugo, 2026-10-08, P-CO-07). */
const PAY_CONCEPTS: CatalogItemApi[] = [{ id: 'pc-uber', code: 'UBER', name: 'Uber' }]

/** Con qué se marca un día del Timesheet sin ponches (Hugo, 2026-10-12).
    Exportado: `timesheetMocks.ts` lo usa para resolver `dayStatusId` al
    marcar un día, sin duplicar la lista. */
export const TIMESHEET_DAY_STATUSES: CatalogItemApi[] = [
  { id: 'tds-rest', code: 'REST', name: 'Descanso' },
  { id: 'tds-off', code: 'OFF', name: 'OFF' },
]

// ── Estados de los dos semáforos, como los sirve la API ──────────────────────
/* Sin En elaboración: desde el 2026-10-29 ninguna requisición vive ahí (nace
   Autorizada y la migración firmó las que esperaban). */
const REQ_STATE: Record<string, StatusRefApi> = {
  GREEN: { code: 'GREEN', color: 'Verde', name: 'Autorizada' },
  YELLOW: { code: 'YELLOW', color: 'Amarillo', name: 'En proceso' },
  LIGHT_BLUE: { code: 'LIGHT_BLUE', color: 'Azul claro', name: 'Cubierta totalmente' },
}

const URGENCY: Record<string, StatusRefApi> = {
  RED: { code: 'RED', color: 'Rojo', name: 'Urgente' },
  YELLOW: { code: 'YELLOW', color: 'Amarillo', name: 'Medio' },
  STRONG_GREEN: { code: 'STRONG_GREEN', color: 'Verde fuerte', name: 'Normal' },
}

const COVERAGE_REF: StatusRefApi = { code: 'RED', color: 'Rojo', name: '0–50%' }

function isoInDays(days: number): string {
  return new Date(Date.now() + days * 86_400_000).toISOString().slice(0, 10)
}

function isoHoursAgo(hours: number): string {
  return new Date(Date.now() - hours * 3_600_000).toISOString()
}

/** RR-H-05, como el back: < 72 h Rojo, 72–120 h Amarillo, > 120 h Verde fuerte. */
function urgencyFor(startDate: string): StatusRefApi {
  const hours = (new Date(startDate).getTime() - Date.now()) / 3_600_000
  return (
    hours < 72 ? URGENCY.RED : hours <= 120 ? URGENCY.YELLOW : URGENCY.STRONG_GREEN
  ) as StatusRefApi
}

type PersonRefApi = NonNullable<RequisitionApi['createdBy']>

/** Quien crea las de los fixtures: el Manager General del hotel. */
const CREATOR: PersonRefApi = { id: 'usr-gm', fullName: 'Gerardo Luna', photoUrl: null }
/** Quien firmaba las viejas, cuando autorizar era un paso aparte. */
const FORMER_AUTHORIZER: PersonRefApi = { id: 'usr-am', fullName: 'Aldo Mena', photoUrl: null }

let positionSequence = 0

function buildPosition(input: {
  positionId: string
  departmentId: string
  quantity: number
  filled: number
  startInDays: number
  urgency: string | null
  englishId?: string
  lineNumber: number
}): RequisitionPositionApi {
  positionSequence += 1
  return {
    id: `dpos-${String(positionSequence).padStart(3, '0')}`,
    lineNumber: input.lineNumber,
    position:
      POSITIONS.find((item) => item.id === input.positionId) ?? (POSITIONS[0] as CatalogItemApi),
    hiringModality: MODALITIES[0] as CatalogItemApi,
    englishLevel: input.englishId
      ? (ENGLISH.find((item) => item.id === input.englishId) ?? null)
      : null,
    department:
      DEPARTMENTS.find((item) => item.id === input.departmentId) ??
      (DEPARTMENTS[0] as CatalogItemApi),
    quantity: input.quantity,
    startDate: isoInDays(input.startInDays),
    startTime: '07:00',
    notes: null,
    coverage: COVERAGE_REF,
    urgency: input.urgency ? (URGENCY[input.urgency] ?? null) : null,
    filled: input.filled,
  }
}

/**
 * Como el back desde el 2026-10-29: la requisición NACE autorizada, con
 * `authorizedAt` = `createdAt` y la firma de quien la creó. `signedHoursAgo`
 * es solo para las viejas, de cuando autorizar era un paso aparte.
 */
function buildRequisition(input: {
  id: string
  number: string
  hotelId: string
  hotelName: string
  state: string
  createdHoursAgo: number
  signedHoursAgo?: number
  createdBy?: PersonRefApi
  positions: RequisitionPositionApi[]
}): RequisitionApi {
  const createdAt = isoHoursAgo(input.createdHoursAgo)
  const creator = input.createdBy ?? CREATOR
  const signer = input.signedHoursAgo === undefined ? creator : FORMER_AUTHORIZER
  return {
    id: input.id,
    number: input.number,
    hotel: {
      id: input.hotelId,
      name: input.hotelName,
      photoUrl: `https://picsum.photos/seed/${input.hotelId}/640/360`,
    },
    createdBy: creator,
    state: REQ_STATE[input.state] ?? (REQ_STATE.GREEN as StatusRefApi),
    areaManagerUserId: null,
    authorizedBy: signer.id,
    authorizer: signer,
    inspector: { id: 'usr-insp', fullName: 'Iván Solís', photoUrl: null },
    authorizedAt:
      input.signedHoursAgo === undefined ? createdAt : isoHoursAgo(input.signedHoursAgo),
    inspectorId: 'usr-insp',
    positions: input.positions,
    totalSlots: input.positions.reduce((total, position) => total + position.quantity, 0),
    filledSlots: input.positions.reduce((total, position) => total + position.filled, 0),
    createdAt,
    updatedAt: null,
  }
}

const requisitions: RequisitionApi[] = [
  // Recién creadas: nacieron Autorizadas (2026-10-29) y nadie las ha tomado.
  buildRequisition({
    id: 'req-0001',
    number: '202608190930·K7',
    hotelId: 'htl-psp-0012',
    hotelName: 'Hotel Puerto Real',
    state: 'GREEN',
    createdHoursAgo: 12,
    positions: [
      buildPosition({
        positionId: 'pos-hk',
        departmentId: 'dep-hk',
        quantity: 6,
        filled: 0,
        startInDays: 2,
        urgency: 'RED',
        lineNumber: 1,
      }),
    ],
  }),
  buildRequisition({
    id: 'req-0002',
    number: '202608200815·B2',
    hotelId: 'htl-psp-0013',
    hotelName: 'Grand Costa Nube',
    state: 'GREEN',
    createdHoursAgo: 20,
    positions: [
      buildPosition({
        positionId: 'pos-ck',
        departmentId: 'dep-fb',
        quantity: 2,
        filled: 0,
        startInDays: 6,
        urgency: 'STRONG_GREEN',
        englishId: 'eng-ba',
        lineNumber: 1,
      }),
      buildPosition({
        positionId: 'pos-hm',
        departmentId: 'dep-hk',
        quantity: 3,
        filled: 0,
        startInDays: 6,
        urgency: 'STRONG_GREEN',
        lineNumber: 2,
      }),
    ],
  }),
  buildRequisition({
    id: 'req-0003',
    number: '202608201110·C9',
    hotelId: 'htl-psp-0014',
    hotelName: 'Hotel Mirador',
    state: 'GREEN',
    createdHoursAgo: 4,
    positions: [
      buildPosition({
        positionId: 'pos-ln',
        departmentId: 'dep-hk',
        quantity: 2,
        filled: 0,
        startInDays: 9,
        urgency: 'STRONG_GREEN',
        lineNumber: 1,
      }),
    ],
  }),
  // Viejas, firmadas aparte horas después de crearse: cobertura parcial y urgencias vivas.
  buildRequisition({
    id: 'req-0004',
    number: '202608120930·K7',
    hotelId: 'htl-psp-0012',
    hotelName: 'Hotel Puerto Real',
    state: 'YELLOW',
    createdHoursAgo: 200,
    signedHoursAgo: 190,
    positions: [
      buildPosition({
        positionId: 'pos-hk',
        departmentId: 'dep-hk',
        quantity: 6,
        filled: 4,
        startInDays: 1,
        urgency: 'RED',
        lineNumber: 1,
      }),
    ],
  }),
  buildRequisition({
    id: 'req-0005',
    number: '202608130800·D4',
    hotelId: 'htl-psp-0015',
    hotelName: 'Villas Coral',
    state: 'YELLOW',
    createdHoursAgo: 180,
    signedHoursAgo: 170,
    positions: [
      buildPosition({
        positionId: 'pos-hm',
        departmentId: 'dep-hk',
        quantity: 4,
        filled: 1,
        startInDays: 2,
        urgency: 'RED',
        lineNumber: 1,
      }),
      buildPosition({
        positionId: 'pos-hk',
        departmentId: 'dep-hk',
        quantity: 2,
        filled: 2,
        startInDays: 4,
        urgency: 'YELLOW',
        lineNumber: 2,
      }),
    ],
  }),
  buildRequisition({
    id: 'req-0006',
    number: '202608140700·E1',
    hotelId: 'htl-psp-0016',
    hotelName: 'Hotel Las Palmas',
    state: 'GREEN',
    createdHoursAgo: 150,
    signedHoursAgo: 140,
    positions: [
      buildPosition({
        positionId: 'pos-ck',
        departmentId: 'dep-fb',
        quantity: 3,
        filled: 0,
        startInDays: 7,
        urgency: 'STRONG_GREEN',
        englishId: 'eng-co',
        lineNumber: 1,
      }),
    ],
  }),
  buildRequisition({
    id: 'req-0007',
    number: '202608150600·F8',
    hotelId: 'htl-psp-0013',
    hotelName: 'Grand Costa Nube',
    state: 'GREEN',
    createdHoursAgo: 120,
    signedHoursAgo: 100,
    positions: [
      buildPosition({
        positionId: 'pos-hk',
        departmentId: 'dep-hk',
        quantity: 5,
        filled: 2,
        startInDays: 8,
        urgency: 'STRONG_GREEN',
        lineNumber: 1,
      }),
    ],
  }),
  buildRequisition({
    id: 'req-0008',
    number: '202608160500·G3',
    hotelId: 'htl-psp-0014',
    hotelName: 'Hotel Mirador',
    state: 'YELLOW',
    createdHoursAgo: 100,
    signedHoursAgo: 90,
    positions: [
      buildPosition({
        positionId: 'pos-ln',
        departmentId: 'dep-hk',
        quantity: 3,
        filled: 1,
        startInDays: 5,
        urgency: 'YELLOW',
        lineNumber: 1,
      }),
    ],
  }),
]

let requisitionSequence = 8

interface CreateBody {
  hotelId?: string
  positions?: Array<{
    catalogPositionId?: string
    hiringModalityId?: string
    hotelDepartmentId?: string
    englishLevelId?: string
    quantity?: number
    startDate?: string
    startTime?: string
  }>
}

/**
 * `coverage.assignment` por posición. Se siembra UNA por cada unidad de
 * `filled` de los fixtures, para que la pantalla de slots y el conteo del
 * tablero cuenten la misma historia.
 */
const OCCUPANT_NAMES = [
  'María Sandoval',
  'José Rivera',
  'Rosa Navarro',
  'Ernesto Lara',
  'Lucía Prado',
  'Hilda Cortés',
]

/** RR-15, modelo colaborativo: quién trabaja la requisición, aparte de sus slots. */
const participantsByRequisition = new Map<string, ParticipantApi[]>()
/** Mismo id que `MOCK_SESSION.user` en `sessionApi.ts` — no se exporta, se repite a propósito. */
const MOCK_ME = { id: 'usr-ana-ruiz', fullName: 'Ana Ruiz', role: { code: 'ROL-V-01', name: 'BD' } }
let participationSequence = 0

const assignmentsByPosition = new Map<string, AssignmentApi[]>()
{
  let occupantIndex = 0
  for (const requisition of requisitions) {
    for (const position of requisition.positions) {
      const seeded: AssignmentApi[] = []
      for (let ordinal = 1; ordinal <= position.filled; ordinal += 1) {
        const name = OCCUPANT_NAMES[occupantIndex % OCCUPANT_NAMES.length] as string
        occupantIndex += 1
        seeded.push({
          id: `asg-seed-${position.id}-${String(ordinal)}`,
          type: 'FIXED',
          status: 'ACTIVE',
          worker: { id: `wrk-seed-${String(occupantIndex)}`, fullName: name },
          slot: { id: `slot-${position.id}-${String(ordinal)}`, ordinal, positionId: position.id },
          createdAt: requisition.createdAt,
        })
      }
      if (seeded.length > 0) assignmentsByPosition.set(position.id, seeded)
    }
  }
}

/** El picker manda ids del Pool (`wrk-0001`…); el nombre real vive allá. */
const PICKER_NAMES: Record<string, string> = {
  'wrk-0001': 'Ana Rivera Gómez',
  'wrk-0002': 'Luis Cabrera',
  'wrk-0003': 'María Fernanda Ortiz',
  'wrk-0004': 'Pedro Alcántara',
  'wrk-0005': 'Julia Mendoza',
}

function mockWorkerNameById(workerId: string): string {
  return PICKER_NAMES[workerId] ?? 'Colaborador'
}

/** Qué lista muta cada `:kind` de la escritura de catálogos del Administrador. */
const CATALOG_FIXTURES: Record<string, CatalogItemApi[] | undefined> = {
  'hotel-departments': DEPARTMENTS,
  industries: INDUSTRIES,
  positions: POSITIONS,
  'hiring-modalities': MODALITIES,
  'english-levels': ENGLISH,
  'pay-concepts': PAY_CONCEPTS,
  'timesheet-day-statuses': TIMESHEET_DAY_STATUSES,
}

const catalogRoute = (path: string, items: CatalogItemApi[]): MockRoute => ({
  method: 'GET',
  path,
  /* COPIAS, no la referencia: RTK congela lo que recibe y los writes de la
     pantalla de catálogos deben poder seguir mutando el fixture. */
  resolve: (): ApiEnvelope<CatalogItemApi[]> => ({ data: items.map((item) => ({ ...item })) }),
})

const routes: readonly MockRoute[] = [
  catalogRoute('/catalogs/hotel-departments', DEPARTMENTS),
  catalogRoute('/catalogs/industries', INDUSTRIES),
  {
    method: 'GET',
    path: '/catalogs/positions',
    /* Como el back real: `?departmentId`/`?industryId` acotan (con AND si
       llegan juntos), y cada puesto dice a cuáles pertenece. */
    resolve: ({
      search,
    }): ApiEnvelope<
      Array<CatalogItemApi & { hotelDepartmentId?: string; industryId?: string }>
    > => {
      const departmentId = search.get('departmentId')
      const industryId = search.get('industryId')
      const rows = POSITIONS.filter(
        (item) =>
          (departmentId === null || POSITION_DEPARTMENT[item.id] === departmentId) &&
          (industryId === null || POSITION_INDUSTRY[item.id] === industryId),
      )
      return {
        data: rows.map((item) => ({
          ...item,
          ...(POSITION_DEPARTMENT[item.id]
            ? { hotelDepartmentId: POSITION_DEPARTMENT[item.id] }
            : {}),
          ...(POSITION_INDUSTRY[item.id] ? { industryId: POSITION_INDUSTRY[item.id] } : {}),
        })),
      }
    },
  },
  {
    method: 'GET',
    path: '/requisitions/positions',
    /* Mock: todos los hoteles de prueba "contratan" el mismo catálogo
       completo — el back real sí lo acota al contrato vigente de `hotelId`
       (2026-10-17), pero replicar un contrato por hotel de fixture no suma
       nada a lo que estas pruebas verifican. */
    resolve: ({
      search,
    }): ApiEnvelope<
      Array<Omit<CatalogItemApi, 'hotelDepartmentId'> & { hotelDepartmentId: string | null }>
    > => ({
      /* Los hoteles sin ciclo comercial no tienen contrato: nada que pedir. */
      data: HOTELS_WITHOUT_CONTRACT.has(search.get('hotelId') ?? '')
        ? []
        : /* Como el back: las que no tienen departamento se piden en General. */
          POSITIONS.map((item) => ({
            ...item,
            hotelDepartmentId: POSITION_DEPARTMENT[item.id] ?? 'dep-general',
          })),
    }),
  },
  /* ——— Escritura de catálogos (pantalla del Administrador). Los fixtures
     viven en este archivo, así que sus writes también. ——— */
  {
    method: 'POST',
    path: '/catalogs/:kind',
    resolve: ({ params, body }): ApiEnvelope<CatalogItemApi> => {
      const { name, industryId, hotelDepartmentId } = body as {
        name: string
        industryId?: string
        hotelDepartmentId?: string
      }
      const list = CATALOG_FIXTURES[params['kind'] ?? '']
      if (!list) throw new Error('Ese catálogo no se administra desde aquí')
      const code = name.toUpperCase().replace(/[^A-Z0-9]+/g, '_')
      if (list.some((item) => item.code === code)) {
        throw new Error('Ya existe una fila con ese nombre en el catálogo')
      }
      if (params['kind'] === 'positions' && !industryId) {
        throw new Error('Una posición pertenece a una industria: elige a cuál')
      }
      const row = { id: `mock-cat-${String(Date.now())}`, code, name }
      list.push(row)
      if (params['kind'] === 'positions' && industryId) {
        POSITION_INDUSTRY[row.id] = industryId
      }
      if (params['kind'] === 'positions' && hotelDepartmentId) {
        POSITION_DEPARTMENT[row.id] = hotelDepartmentId
      }
      // Copia: RTK congela lo que recibe y el fixture debe seguir mutable.
      return { data: { ...row } }
    },
  },
  {
    method: 'PATCH',
    path: '/catalogs/:kind/:id',
    resolve: ({ params, body }): ApiEnvelope<CatalogItemApi> => {
      const list = CATALOG_FIXTURES[params['kind'] ?? '']
      const row = list?.find((item) => item.id === params['id'])
      if (!row) throw new Error('Esa fila del catálogo no existe')
      const { name, industryId, hotelDepartmentId } = body as {
        name?: string
        industryId?: string
        hotelDepartmentId?: string
      }
      if (name !== undefined) {
        row.name = name
        row.code = name.toUpperCase().replace(/[^A-Z0-9]+/g, '_')
      }
      if (params['kind'] === 'positions' && industryId) {
        POSITION_INDUSTRY[row.id] = industryId
      }
      if (params['kind'] === 'positions' && hotelDepartmentId) {
        POSITION_DEPARTMENT[row.id] = hotelDepartmentId
      }
      return { data: { ...row } }
    },
  },
  {
    method: 'DELETE',
    path: '/catalogs/:kind/:id',
    resolve: ({ params }): { ok: true } => {
      const kind = params['kind'] ?? ''
      const list = CATALOG_FIXTURES[kind]
      const index = list?.findIndex((item) => item.id === params['id']) ?? -1
      if (!list || index === -1) throw new Error('Esa fila del catálogo no existe')
      /* La FK del back, simulada: un departamento con puestos no se elimina. */
      if (
        kind === 'hotel-departments' &&
        Object.values(POSITION_DEPARTMENT).includes(params['id'] ?? '')
      ) {
        throw new Error(
          'El departamento tiene posiciones u operación colgando de él: no se puede eliminar',
        )
      }
      list.splice(index, 1)
      return { ok: true }
    },
  },
  catalogRoute('/catalogs/hiring-modalities', MODALITIES),
  catalogRoute('/catalogs/english-levels', ENGLISH),
  catalogRoute('/catalogs/pay-concepts', PAY_CONCEPTS),
  catalogRoute('/catalogs/timesheet-day-statuses', TIMESHEET_DAY_STATUSES),
  {
    method: 'GET',
    path: '/requisitions',
    resolve: ({ search }): PaginatedEnvelope<RequisitionApi> => {
      const state = search.get('state')
      const items = requisitions.filter((item) => !state || item.state.code === state)
      return { data: items, meta: { page: 1, limit: 100, total: items.length, totalPages: 1 } }
    },
  },
  {
    method: 'GET',
    path: '/requisitions/:requisitionId',
    resolve: ({ params }): ApiEnvelope<RequisitionApi> => {
      const found = requisitions.find((item) => item.id === params.requisitionId)
      if (!found) throw new Error('REQUISITION_NOT_FOUND')
      /* Como el back (2026-10-27): los puestos con tarifa en el contrato vigente. */
      return {
        data: {
          ...found,
          contractedCatalogPositionIds: HOTELS_WITHOUT_CONTRACT.has(found.hotel.id)
            ? []
            : POSITIONS.map((item) => item.id),
        },
      }
    },
  },
  {
    method: 'GET',
    path: '/requisitions/:requisitionId/assignments',
    resolve: ({ params }): ApiEnvelope<AssignmentApi[]> => {
      const found = requisitions.find((item) => item.id === params.requisitionId)
      if (!found) throw new Error('REQUISITION_NOT_FOUND')
      return {
        data: found.positions.flatMap((position) =>
          (assignmentsByPosition.get(position.id) ?? []).map((item) => ({ ...item })),
        ),
      }
    },
  },
  {
    method: 'GET',
    path: '/requisitions/:requisitionId/journal',
    /**
     * `journal.journal`, en juguete: creación siempre; la autorización como
     * evento aparte solo en las viejas, firmadas después de crearse. Las que
     * nacen autorizadas lo dicen en el payload de su creación
     * (`autoAuthorized`), como el back. Del más reciente al más viejo, como
     * la consulta real (`occurred_at desc`).
     */
    resolve: ({ params }): ApiEnvelope<RequisitionJournalEntryApi[]> => {
      const found = requisitions.find((item) => item.id === params.requisitionId)
      if (!found) throw new Error('REQUISITION_NOT_FOUND')

      const signedApart = found.authorizedAt !== null && found.authorizedAt !== found.createdAt
      const entries: RequisitionJournalEntryApi[] = [
        {
          id: `jrn-${found.id}-created`,
          eventType: 'REQUISITION_CREATED',
          actorName: found.createdBy?.fullName ?? null,
          actorRole: 'ROL-H-01',
          payload: {
            number: found.number,
            positions: found.positions.length,
            ...(signedApart ? {} : { autoAuthorized: true }),
          },
          occurredAt: found.createdAt,
        },
      ]

      if (signedApart && found.authorizedAt) {
        entries.push({
          id: `jrn-${found.id}-authorized`,
          eventType: 'REQUISITION_AUTHORIZED',
          actorName: 'Gerardo Luna',
          actorRole: 'ROL-H-03',
          payload: { positions: found.positions.length },
          occurredAt: found.authorizedAt,
        })
      }

      return { data: entries.sort((a, b) => (a.occurredAt < b.occurredAt ? 1 : -1)) }
    },
  },
  {
    method: 'POST',
    path: '/assignments',
    /**
     * RR-15 en versión mock: el motor real lo impone con `FOR UPDATE SKIP
     * LOCKED` y aquí se imita la consecuencia — sin slot libre, 409 en texto.
     */
    resolve: ({
      body,
    }): ApiEnvelope<{
      assignment: AssignmentApi
      positionCoverage: string
      requisitionState: string
    }> => {
      const payload = (body ?? {}) as {
        positionId?: string
        workerId?: string
        type?: string
        endDate?: string
      }
      const requisition = requisitions.find((item) =>
        item.positions.some((position) => position.id === payload.positionId),
      )
      const position = requisition?.positions.find((item) => item.id === payload.positionId)
      if (!requisition || !position) throw new Error('POSITION_NOT_FOUND')
      /* Mismo candado que el back real (`assignments.service.ts`): sin esto,
         el mock dejaba pasar cualquier asignación aunque la requisición
         siguiera GREEN (sin tomar), y nada detectaba la regresión del
         2026-09-28. */
      if (requisition.state.code !== 'YELLOW') throw new Error('REQUISITION_NOT_IN_PROGRESS')
      const taken = assignmentsByPosition.get(position.id) ?? []
      if (taken.length >= position.quantity) {
        throw new Error('Otra reclutadora ganó el slot (RR-15): ya no quedan libres')
      }
      const created: AssignmentApi = {
        id: `asg-${String(taken.length + 1)}-${position.id}`,
        type: payload.type ?? 'FIXED',
        status: 'ACTIVE',
        worker: {
          id: payload.workerId ?? '',
          fullName: mockWorkerNameById(payload.workerId ?? ''),
        },
        slot: {
          id: `slot-${position.id}-${String(taken.length + 1)}`,
          ordinal: taken.length + 1,
          positionId: position.id,
        },
        createdAt: new Date().toISOString(),
      }
      assignmentsByPosition.set(position.id, [...taken, created])
      position.filled += 1
      requisition.filledSlots += 1
      /** El semáforo de posiciones, en su versión de juguete: lleno = azul. */
      const isFull = position.filled >= position.quantity
      return {
        data: {
          assignment: { ...created },
          positionCoverage: isFull ? 'LIGHT_BLUE' : 'YELLOW',
          requisitionState: requisition.state.code,
        },
      }
    },
  },
  {
    method: 'DELETE',
    path: '/assignments/:assignmentId',
    resolve: ({ params }): ApiEnvelope<AssignmentApi> => {
      const assignmentId = params.assignmentId ?? ''
      for (const [positionId, taken] of assignmentsByPosition) {
        const found = taken.find((item) => item.id === assignmentId)
        if (!found) continue
        found.status = 'RELEASED'
        assignmentsByPosition.set(
          positionId,
          taken.filter((item) => item.id !== assignmentId),
        )
        const position = requisitions
          .flatMap((item) => item.positions)
          .find((item) => item.id === positionId)
        const requisition = requisitions.find((item) =>
          item.positions.some((p) => p.id === positionId),
        )
        if (position) position.filled = Math.max(0, position.filled - 1)
        if (requisition) requisition.filledSlots = Math.max(0, requisition.filledSlots - 1)
        return { data: found }
      }
      throw new Error('ASSIGNMENT_NOT_FOUND')
    },
  },
  {
    method: 'GET',
    path: '/requisitions/:requisitionId/participants',
    resolve: ({ params }): ApiEnvelope<ParticipantApi[]> => ({
      data: participantsByRequisition.get(params.requisitionId ?? '') ?? [],
    }),
  },
  {
    method: 'POST',
    path: '/requisitions/:requisitionId/participants',
    resolve: ({ params }): ApiEnvelope<ParticipationResultApi> => {
      const requisitionId = params.requisitionId ?? ''
      const requisition = requisitions.find((item) => item.id === requisitionId)
      if (!requisition) throw new Error('REQUISITION_NOT_FOUND')
      const current = participantsByRequisition.get(requisitionId) ?? []
      if (current.some((item) => item.user.id === MOCK_ME.id)) {
        throw new Error('ALREADY_PARTICIPATING')
      }
      participationSequence += 1
      const created: ParticipantApi = {
        id: `part-${String(participationSequence)}`,
        user: MOCK_ME,
        joinedAt: new Date().toISOString(),
      }
      const updated = [...current, created]
      participantsByRequisition.set(requisitionId, updated)
      if (requisition.state.code === 'GREEN') requisition.state.code = 'YELLOW'
      return { data: { requisitionState: requisition.state.code, participants: updated } }
    },
  },
  {
    method: 'DELETE',
    path: '/requisitions/:requisitionId/participants/me',
    resolve: ({ params }): ApiEnvelope<ParticipationResultApi> => {
      const requisitionId = params.requisitionId ?? ''
      const requisition = requisitions.find((item) => item.id === requisitionId)
      if (!requisition) throw new Error('REQUISITION_NOT_FOUND')
      const current = participantsByRequisition.get(requisitionId) ?? []
      if (!current.some((item) => item.user.id === MOCK_ME.id)) {
        throw new Error('NOT_PARTICIPATING')
      }
      const updated = current.filter((item) => item.user.id !== MOCK_ME.id)
      participantsByRequisition.set(requisitionId, updated)
      if (updated.length === 0 && requisition.state.code === 'YELLOW') {
        requisition.state.code = 'GREEN'
      }
      return { data: { requisitionState: requisition.state.code, participants: updated } }
    },
  },
  {
    method: 'POST',
    path: '/requisitions',
    resolve: ({ body }): ApiEnvelope<RequisitionApi> => {
      const payload = (body ?? {}) as CreateBody
      if (!payload.hotelId || !payload.positions?.length) throw new Error('Faltan datos')
      requisitionSequence += 1
      positionSequence += 100
      /* Nace Autorizada (2026-10-29): la firma es de quien la crea y cada
         posición nace con su urgencia, contra su fecha de inicio. */
      const created = buildRequisition({
        id: `req-${String(requisitionSequence).padStart(4, '0')}`,
        number: `20260821${String(requisitionSequence).padStart(4, '0')}·N${String(requisitionSequence % 10)}`,
        hotelId: payload.hotelId,
        hotelName: 'Hotel nuevo',
        state: 'GREEN',
        createdHoursAgo: 0,
        createdBy: { id: MOCK_ME.id, fullName: MOCK_ME.fullName, photoUrl: null },
        positions: payload.positions.map((position, index) =>
          buildPosition({
            positionId: position.catalogPositionId ?? 'pos-hk',
            departmentId: position.hotelDepartmentId ?? 'dep-hk',
            quantity: position.quantity ?? 1,
            filled: 0,
            startInDays: 7,
            urgency: null,
            ...(position.englishLevelId ? { englishId: position.englishLevelId } : {}),
            lineNumber: index + 1,
          }),
        ),
      })
      for (const [index, position] of created.positions.entries()) {
        const requested = payload.positions[index]?.startDate
        if (requested) position.startDate = requested
        position.urgency = urgencyFor(position.startDate)
      }
      requisitions.unshift(created)
      return { data: created }
    },
  },
  {
    method: 'POST',
    path: '/requisitions/:requisitionId/delete',
    /* Como el back: sin borrador, el motivo va siempre (`REASON_REQUIRED`).
       Quién puede lo decide el back por estado y creador; el mock no lo
       replica. */
    resolve: ({ params, body }): { data: { deleted: true } } => {
      if (!requisitions.some((item) => item.id === params.requisitionId)) {
        throw new Error('REQUISITION_NOT_FOUND')
      }
      const { reason } = (body ?? {}) as { reason?: string }
      if (!reason?.trim()) throw new Error('Eliminar una requisición exige un motivo')
      return { data: { deleted: true } }
    },
  },
]

let areRoutesRegistered = false

export function registerRequisitionsMocks(): void {
  if (areRoutesRegistered) return
  areRoutesRegistered = true
  registerMockRoutes(routes)
  // `/hotels` y `/me`, que la composición consume, viven en otras features.
  registerOnboardingMocks()
}
